import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/formatters';
import './MatchContextModal.css';

const RESULT_COLOR = { W: '#16a34a', D: '#f59e0b', L: '#ef4444' };
const RESULT_CLASS = { W: 'win', D: 'draw', L: 'loss' };
const PROB_COLOR   = { H: '#3b82f6', D: '#f59e0b', A: '#ef4444' };

function FormBadge({ result }) {
  const { t } = useTranslation();
  const labels = {
    W: t('results.H_short')[0],   // "L" / "H" / "D" según idioma
    D: t('results.D_short')[0],   // "E" / "D" / "N"
    L: t('results.A_short')[0],   // "V" / "A" / "E"
  };
  return (
    <span className="mcm-badge" style={{ background: RESULT_COLOR[result] ?? '#9ca3af' }}>
      {labels[result] ?? result}
    </span>
  );
}

function MiniMatch({ match, perspectiveTeam }) {
  const { t } = useTranslation();
  const isHome   = match.home_team === perspectiveTeam;
  const opponent = isHome ? match.away_team : match.home_team;
  const score    = match.home_score != null
    ? `${match.home_score}–${match.away_score}` : '–';
  const tr = match.team_result;

  return (
    <div className={`mcm-mini mcm-mini--${RESULT_CLASS[tr] ?? 'draw'}`}>
      <span className="mcm-mini__date">{formatDate(match.date)}</span>
      <span className="mcm-mini__loc">
        {isHome ? t('match_context.home_indicator') : t('match_context.away_indicator')}
      </span>
      <span className="mcm-mini__opp">{opponent}</span>
      <span className="mcm-mini__score">{score}</span>
      <FormBadge result={tr} />
    </div>
  );
}

function H2HMatch({ match }) {
  const score = match.home_score != null
    ? `${match.home_score}–${match.away_score}` : '–';

  const isDraw  = match.result === 'D';
  const winner  = match.result === 'H' ? match.home_team
                : match.result === 'A' ? match.away_team
                : null;

  return (
    <div className={`mcm-mini mcm-mini--${isDraw ? 'draw' : 'decided'}`}>
      <span className="mcm-mini__date">{formatDate(match.date)}</span>
      <span className="mcm-mini__opp mcm-mini__opp--h2h">
        <span className={match.result === 'H' ? 'mcm-h2h-winner' : 'mcm-h2h-loser'}>
          {match.home_team}
        </span>
        <em>vs</em>
        <span className={match.result === 'A' ? 'mcm-h2h-winner' : 'mcm-h2h-loser'}>
          {match.away_team}
        </span>
      </span>
      <span className="mcm-mini__score">{score}</span>
      {isDraw
        ? <span className="mcm-badge" style={{ background: '#f59e0b' }}>E</span>
        : <span className="mcm-h2h-winner-badge">{winner?.split(' ').pop()}</span>
      }
    </div>
  );
}

function FormRow({ summary, team }) {
  const { t } = useTranslation();
  return (
    <div className="mcm-form-row">
      <span className="mcm-form-row__team">{team}</span>
      <div className="mcm-form-row__badges">
        {(summary.form_string || '').split('').map((r, i) => (
          <FormBadge key={i} result={r} />
        ))}
        {summary.form_string === '' && (
          <span className="mcm-empty-text">{t('match_context.no_prev_matches')}</span>
        )}
      </div>
      <span className="mcm-form-row__pts">{summary.points} {t('match_context.pts')}</span>
    </div>
  );
}

export function MatchContextModal({ match, onClose, fetchMatchContext }) {
  const { t } = useTranslation();
  const [context, setContext]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('forma');

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (!match?.match_id) return;
    setLoading(true);
    setContext(null);
    setActiveTab('forma');
    fetchMatchContext(match.match_id)
      .then(setContext)
      .finally(() => setLoading(false));
  }, [match?.match_id]);

  if (!match) return null;

  const pred    = match.prediction ?? match;
  const probH   = Math.round((pred.probabilities?.home ?? pred.prob_home ?? 0) * 100);
  const probD   = Math.round((pred.probabilities?.draw ?? pred.prob_draw ?? 0) * 100);
  const probA   = Math.round((pred.probabilities?.away ?? pred.prob_away ?? 0) * 100);
  const conf    = Math.round((pred.confidence ?? 0) * 100);
  const predResult = pred.predicted_result;

  const winnerLabel = predResult === 'H' ? match.home_team
                    : predResult === 'A' ? match.away_team
                    : t('match_context.draw_result');

  const tabs = [
    { id: 'forma', label: t('match_context.tab_form') },
    { id: 'h2h',   label: t('match_context.tab_h2h') },
  ];

  return (
    <div className="mcm-overlay" onClick={onClose}>
      <div className="mcm-box" onClick={(e) => e.stopPropagation()}>

        {/* HEADER */}
        <div className="mcm-header">
          <div className="mcm-header__league">
            {match.league_name ?? t(`leagues.${match.league}`, { defaultValue: match.league })}
          </div>
          <button className="mcm-close" onClick={onClose}>✕</button>
        </div>

        {/* EQUIPOS + FECHA */}
        <div className="mcm-teams">
          <div className="mcm-team mcm-team--home">{match.home_team}</div>
          <div className="mcm-team-sep">{t('common.vs')}</div>
          <div className="mcm-team mcm-team--away">{match.away_team}</div>
        </div>
        <div className="mcm-date">{formatDate(match.date)}</div>

        {/* PREDICCIÓN RESUMIDA */}
        {predResult && (
          <div className="mcm-pred-strip">
            <div className="mcm-pred-bar">
              <div className="mcm-pred-bar__seg" style={{ width: `${probH}%`, background: PROB_COLOR.H }} />
              <div className="mcm-pred-bar__seg" style={{ width: `${probD}%`, background: PROB_COLOR.D }} />
              <div className="mcm-pred-bar__seg" style={{ width: `${probA}%`, background: PROB_COLOR.A }} />
            </div>
            <div className="mcm-pred-labels">
              <span>
                <span className="mcm-pred-dot" style={{ background: PROB_COLOR.H }} />
                {match.home_team} {probH}%
              </span>
              <span>
                <span className="mcm-pred-dot" style={{ background: PROB_COLOR.D }} />
                {t('results.D_short')} {probD}%
              </span>
              <span>
                <span className="mcm-pred-dot" style={{ background: PROB_COLOR.A }} />
                {match.away_team} {probA}%
              </span>
            </div>
            <div className="mcm-pred-conclusion">
              <span>{t('match_context.predicted')}:</span>
              <strong>{winnerLabel}</strong>
              <span className="mcm-conf-badge">{conf}% {t('match_context.confidence')}</span>
            </div>
          </div>
        )}

        {/* TABS */}
        <div className="mcm-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`mcm-tab ${activeTab === tab.id ? 'mcm-tab--active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* CONTENIDO */}
        <div className="mcm-body">
          {loading && <p className="mcm-loading">{t('match_context.loading_form')}</p>}

          {!loading && !context && (
            <p className="mcm-empty">{t('match_context.no_data')}</p>
          )}

          {/* ── FORMA ── */}
          {!loading && context && activeTab === 'forma' && (
            <>
              <div className="mcm-form-summary">
                <FormRow summary={context.home_form.summary} team={context.home_team} />
                <FormRow summary={context.away_form.summary} team={context.away_team} />
              </div>

              <div className="mcm-block">
                <h4 className="mcm-block__title">
                  {context.home_team} — {t('match_context.last_5')}
                </h4>
                {context.home_form.matches.length === 0
                  ? <p className="mcm-empty">{t('match_context.no_prev_matches')}</p>
                  : context.home_form.matches.map((m, i) => (
                      <MiniMatch key={i} match={m} perspectiveTeam={context.home_team} />
                    ))
                }
              </div>

              <div className="mcm-block">
                <h4 className="mcm-block__title">
                  {context.away_team} — {t('match_context.last_5')}
                </h4>
                {context.away_form.matches.length === 0
                  ? <p className="mcm-empty">{t('match_context.no_prev_matches')}</p>
                  : context.away_form.matches.map((m, i) => (
                      <MiniMatch key={i} match={m} perspectiveTeam={context.away_team} />
                    ))
                }
              </div>
            </>
          )}

          {/* ── H2H ── */}
          {!loading && context && activeTab === 'h2h' && (
            <>
              <div className="mcm-h2h-summary">
                <div className="mcm-h2h-col">
                  <span className="mcm-h2h-num">{context.h2h.home_wins}</span>
                  <span className="mcm-h2h-label">{context.home_team}</span>
                </div>
                <div className="mcm-h2h-col mcm-h2h-col--draw">
                  <span className="mcm-h2h-num">{context.h2h.draws}</span>
                  <span className="mcm-h2h-label">{t('match_context.draws')}</span>
                </div>
                <div className="mcm-h2h-col">
                  <span className="mcm-h2h-num">{context.h2h.away_wins}</span>
                  <span className="mcm-h2h-label">{context.away_team}</span>
                </div>
              </div>

              <div className="mcm-block">
                <h4 className="mcm-block__title">{t('match_context.h2h_title')}</h4>
                {context.h2h.matches.length === 0
                  ? <p className="mcm-empty">{t('match_context.no_h2h')}</p>
                  : context.h2h.matches.map((m, i) => (
                      <H2HMatch key={i} match={m} homeTeam={context.home_team} />
                    ))
                }
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}